import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, Send, Mail } from 'lucide-react';

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast.success('Message sent — we\'ll get back to you soon.');
    }, 400);
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <PageHero
        eyebrow="Company"
        title="Contact"
        description="Questions, feedback, or partnership inquiries — we'd like to hear from you."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-5 gap-10 max-w-4xl mx-auto">
            <div className="md:col-span-2">
              <h2 className="font-chivo text-lg font-bold uppercase tracking-tight mb-3">
                Other Ways to Reach Us
              </h2>
              <div className="flex items-start gap-3 text-sm text-muted-foreground mb-4">
                <Mail className="h-4 w-4 text-autoxium-blue shrink-0 mt-0.5" />
                <span><a href="mailto:contact@autoxium.com" className="hover:text-autoxium-blue">contact@autoxium.com</a></span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Privacy or accessibility related? Mention it in your message and it'll be
                routed to the right team — or check our{' '}
                <Link to="/privacy-policy" className="text-autoxium-blue font-medium">Privacy Policy</Link>{' '}
                and{' '}
                <Link to="/accessibility" className="text-autoxium-blue font-medium">Accessibility Statement</Link>{' '}
                first.
              </p>
            </div>

            <div className="md:col-span-3">
              <Card className="technical-card">
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-8" data-testid="contact-success">
                      <CheckCircle2 className="h-12 w-12 text-autoxium-blue mx-auto mb-4" />
                      <h2 className="font-chivo text-xl font-bold uppercase tracking-tight mb-2">
                        Message Sent
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Thanks for reaching out — we typically respond within a few
                        business days.
                      </p>
                      <Button
                        variant="outline"
                        className="font-bold uppercase tracking-wider"
                        onClick={() => {
                          setForm({ name: '', email: '', subject: '', message: '' });
                          setSubmitted(false);
                        }}
                        data-testid="contact-another-btn"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="contact-name">Name</Label>
                          <Input
                            id="contact-name"
                            value={form.name}
                            onChange={handleChange('name')}
                            placeholder="Jane Doe"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contact-email">Email</Label>
                          <Input
                            id="contact-email"
                            type="email"
                            value={form.email}
                            onChange={handleChange('email')}
                            placeholder="you@email.com"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="contact-subject">Subject</Label>
                        <Input
                          id="contact-subject"
                          value={form.subject}
                          onChange={handleChange('subject')}
                          placeholder="What's this about?"
                          data-testid="contact-subject-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="contact-message">Message</Label>
                        <Textarea
                          id="contact-message"
                          rows={5}
                          value={form.message}
                          onChange={handleChange('message')}
                          placeholder="How can we help?"
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                        data-testid="contact-submit-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {submitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
